import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';

const LS_PREFIX = 'sectionPanelCollapsed';

function storageKeyOf(prefsUserId, storageKey) {
  return `${LS_PREFIX}|${prefsUserId || 'anon'}|${storageKey}`;
}

function readCollapsed(prefsUserId, storageKey, defaultCollapsed) {
  if (!storageKey) return defaultCollapsed;
  try {
    const raw = localStorage.getItem(storageKeyOf(prefsUserId, storageKey));
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* ignore */
  }
  return defaultCollapsed;
}

function writeCollapsed(prefsUserId, storageKey, collapsed) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKeyOf(prefsUserId, storageKey), collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  flex: ${(p) => (p.$collapsed ? '0 0 auto' : p.$flex || '0 0 auto')};
  /* expandedHeight: 형제 공간을 나눠 갖지 않고 아래로 펼쳐지는(문서 흐름) 모드 */
  height: ${(p) => (p.$collapsed ? 'auto' : p.$expandedHeight || 'auto')};
  max-height: ${(p) => (p.$collapsed ? 'none' : p.$expandedMaxHeight || 'none')};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  min-height: 28px;
  padding: 2px 8px 2px 10px;
  background: ${(p) =>
    p.$collapsed ? theme.colors.gray?.[50] || '#f9fafb' : theme.colors.white};
  border-bottom: ${(p) => (p.$collapsed ? 'none' : `1px solid ${theme.colors.border}`)};
  cursor: default;
`;

const BarLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const Summary = styled.span`
  font-weight: 400;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToggleBtn = styled.button`
  appearance: none;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  padding: 2px 8px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.xs};
  cursor: pointer;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Body = styled.div`
  display: ${(p) => (p.$collapsed ? 'none' : 'flex')};
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

const ChevronIcon = ({ up = false }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <polyline points={up ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
  </svg>
);

/**
 * 등록화면 하단 「내 단계 목록」 등 — 접을 수 있는 섹션 패널
 * - 접어도 children 유지 (그리드 데이터·선택 유지)
 * - Alt+F1은 검색조건 전용 — 본 패널은 클릭 토글만
 */
const CollapsibleSectionPanel = ({
  storageKey,
  prefsUserId = '',
  title = '목록',
  summary = '',
  defaultCollapsed = true,
  expandedMaxHeight = '260px',
  expandedHeight,
  flex,
  children,
  headerExtra = null,
}) => {
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsed(prefsUserId, storageKey, defaultCollapsed)
  );
  const shellRef = useRef(null);

  useEffect(() => {
    setCollapsed(readCollapsed(prefsUserId, storageKey, defaultCollapsed));
  }, [prefsUserId, storageKey, defaultCollapsed]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(prefsUserId, storageKey, next);
      return next;
    });
  }, [prefsUserId, storageKey]);

  const barText = useMemo(() => {
    if (!collapsed) return title;
    return (
      <>
        {title}
        {summary ? <Summary>· {summary}</Summary> : null}
      </>
    );
  }, [collapsed, title, summary]);

  return (
    <Shell
      ref={shellRef}
      $collapsed={collapsed}
      $expandedMaxHeight={expandedMaxHeight}
      $expandedHeight={expandedHeight}
      $flex={flex}
      data-section-panel={collapsed ? 'collapsed' : 'expanded'}
    >
      <Bar $collapsed={collapsed}>
        <BarLabel title={typeof summary === 'string' ? String(summary) : undefined}>
          {barText}
        </BarLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {headerExtra}
          <ToggleBtn
            type="button"
            onClick={toggle}
            title={collapsed ? '펼치기' : '접기'}
            aria-expanded={!collapsed}
            aria-label={collapsed ? '펼치기' : '접기'}
          >
            {collapsed ? (
              <>
                펼치기 <ChevronIcon />
              </>
            ) : (
              <>
                접기 <ChevronIcon up />
              </>
            )}
          </ToggleBtn>
        </div>
      </Bar>
      <Body $collapsed={collapsed} aria-hidden={collapsed}>
        {children}
      </Body>
    </Shell>
  );
};

export default CollapsibleSectionPanel;
